import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { dashboardApi, pipelineApi, targetsApi, outreachApi, grantsApi, formatApiError } from '../lib/api';
import { pollJob } from '../lib/pollJob';
import { HelpButton } from '../components/HelpPanel';
import { useAuth } from '../contexts/AuthContext';
import { WriteOnly } from '../components/ViewerGuard';
import { 
  Target, 
  Users, 
  AlertCircle, 
  TrendingUp,
  ArrowRight,
  Clock,
  Flame,
  Snowflake,
  PoundSterling,
  Percent,
  Trophy,
  Upload,
  FileText,
  Loader2,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  CalendarDays,
  Zap,
  MessageCircle,
  X,
  Edit3,
  Send,
  DollarSign,
  Radar,
  AlertTriangle,
  ExternalLink,
  UserCircle,
  Activity,
  Pause,
  Search,
  Heart,
  ShieldAlert
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import WelcomeModal from '../components/WelcomeModal';
import usePageTitle from '../hooks/usePageTitle';

// Pipeline categories (six white-label defaults) — drives the quick-add stream selector.
const BD_STREAMS = [
  'Clients', 'Prospective Clients', 'Investors',
  'Grants & Funding', 'Partnerships', 'Other / Unclassified',
];

const QUICK_ADD_STEPS = [
  { label: 'Creating pipeline record', key: 'pipeline' },
  { label: 'Running research', key: 'research' },
  { label: 'Drafting outreach', key: 'outreach' },
];

const getScoreColor = (score) => {
  if (score >= 71) return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' };
  if (score >= 41) return { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' };
  return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' };
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [recentTargets, setRecentTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [copied, setCopied] = useState(false);
  // Follow-Up Engine state
  const [reEngageLoading, setReEngageLoading] = useState(null);
  const [reEngageDraft, setReEngageDraft] = useState(null);
  const [reEngageEditing, setReEngageEditing] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  // Grant digest state
  const [grantDigest, setGrantDigest] = useState(null);
  // Team activity state
  const [teamActivity, setTeamActivity] = useState([]);
  // Quick Add state
  const [qaInput, setQaInput] = useState('');
  const [qaBdStream, setQaBdStream] = useState('Clients');
  const [qaShowStreams, setQaShowStreams] = useState(false);
  const [qaJobId, setQaJobId] = useState(null);
  const [qaStatus, setQaStatus] = useState(null);
  // Relationship Health state
  const [relHealth, setRelHealth] = useState(null);
  // TEMP diagnostic (deal_value source audit) — remove after review, do NOT ship to golden template
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError] = useState('');
  const [wipeResult, setWipeResult] = useState(null);
  const [wipeLoading, setWipeLoading] = useState(false);
  const [fieldAudit, setFieldAudit] = useState(null);
  const [fieldAuditLoading, setFieldAuditLoading] = useState(false);
  const [ownerMigrateResult, setOwnerMigrateResult] = useState(null);
  const [ownerMigrateLoading, setOwnerMigrateLoading] = useState(false);
  const [catAudit, setCatAudit] = useState(null);
  const [catAuditLoading, setCatAuditLoading] = useState(false);
  const [catMigrateResult, setCatMigrateResult] = useState(null);
  const [catMigrateLoading, setCatMigrateLoading] = useState(false);
  const [schemaAudit, setSchemaAudit] = useState(null);
  const [schemaAuditLoading, setSchemaAuditLoading] = useState(false);
  const [schemaMigrateResult, setSchemaMigrateResult] = useState(null);
  const [schemaMigrateLoading, setSchemaMigrateLoading] = useState(false);
  const [cleanupAudit, setCleanupAudit] = useState(null);
  const [cleanupAuditLoading, setCleanupAuditLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const { user, isAdmin } = useAuth();

  const runDealValueDiagnostic = async () => {
    setDiagOpen(true);
    setDiagLoading(true);
    setDiagError('');
    setWipeResult(null);
    try {
      const res = await pipelineApi.dealValueDiagnostic();
      setDiagData(res.data);
    } catch (err) {
      setDiagError(formatApiError(err));
    } finally {
      setDiagLoading(false);
    }
  };

  const runDealValueWipe = async () => {
    if (!window.confirm('WIPE: null out deal_value on every non-zero record that is below Proposal Sent stage. This runs against the CURRENTLY CONNECTED database. Continue?')) return;
    setWipeLoading(true);
    setDiagError('');
    try {
      const res = await pipelineApi.wipePreproposalDealValues();
      setWipeResult(res.data);
      // refresh the diagnostic numbers after wipe
      const d = await pipelineApi.dealValueDiagnostic();
      setDiagData(d.data);
    } catch (err) {
      setDiagError(formatApiError(err));
    } finally {
      setWipeLoading(false);
    }
  };

  const runFieldAudit = async () => {
    setFieldAuditLoading(true);
    setDiagError('');
    try {
      const res = await pipelineApi.fieldAudit();
      setFieldAudit(res.data);
    } catch (err) {
      setDiagError(formatApiError(err));
    } finally {
      setFieldAuditLoading(false);
    }
  };

  const runOwnerMigration = async () => {
    if (!window.confirm('MIGRATE: rewrite every target and pipeline record owner to its canonical full name, on the CONNECTED database. Continue?')) return;
    setOwnerMigrateLoading(true);
    setDiagError('');
    try {
      const res = await pipelineApi.migrateOwnerCanonical();
      setOwnerMigrateResult(res.data);
    } catch (err) {
      setDiagError(formatApiError(err));
    } finally {
      setOwnerMigrateLoading(false);
    }
  };

  const runCategoryAudit = async () => {
    setCatAuditLoading(true);
    setDiagError('');
    setCatMigrateResult(null);
    try {
      const res = await pipelineApi.migrateCategories(false);
      setCatAudit(res.data);
    } catch (err) {
      setDiagError(formatApiError(err));
    } finally {
      setCatAuditLoading(false);
    }
  };

  const runCategoryMigration = async () => {
    if (!catAudit) return;
    if (!window.confirm('APPLY category migration using the PROPOSED mapping above, on the CONNECTED database. This sets `category` on every record and removes the dead `business_line`. Continue?')) return;
    setCatMigrateLoading(true);
    setDiagError('');
    try {
      const res = await pipelineApi.migrateCategories(true, catAudit.proposed_mapping || {});
      setCatMigrateResult(res.data);
    } catch (err) {
      setDiagError(formatApiError(err));
    } finally {
      setCatMigrateLoading(false);
    }
  };

  const runSchemaAudit = async () => {
    setSchemaAuditLoading(true);
    setDiagError('');
    setSchemaMigrateResult(null);
    try {
      const res = await pipelineApi.migrateSchemaKeys(false);
      setSchemaAudit(res.data);
    } catch (err) {
      setDiagError(formatApiError(err));
    } finally {
      setSchemaAuditLoading(false);
    }
  };

  const runSchemaMigration = async () => {
    if (!schemaAudit) return;
    if (!window.confirm('APPLY schema-key migration on the CONNECTED database: rename in_depth_fit_assessment → fit_assessment on target research and intelligence_suite_fit → offering_fit on briefings. Renderers dual-read, so the UI is unchanged. Continue?')) return;
    setSchemaMigrateLoading(true);
    setDiagError('');
    try {
      const res = await pipelineApi.migrateSchemaKeys(true);
      setSchemaMigrateResult(res.data);
    } catch (err) {
      setDiagError(formatApiError(err));
    } finally {
      setSchemaMigrateLoading(false);
    }
  };

  const runCleanupAudit = async () => {
    setCleanupAuditLoading(true);
    setDiagError('');
    setCleanupResult(null);
    try {
      const res = await pipelineApi.cleanupVendorValues(false);
      setCleanupAudit(res.data);
    } catch (err) {
      setDiagError(formatApiError(err));
    } finally {
      setCleanupAuditLoading(false);
    }
  };

  const runCleanupApply = async () => {
    if (!cleanupAudit) return;
    const staleList = (cleanupAudit.stale_set || []).join(', ') || '(none)';
    const w1n = cleanupAudit.w1_relevant_projects?.count ?? 0;
    const w2n = cleanupAudit.w2_suggestion_text?.count ?? 0;
    if (!window.confirm(`APPLY data cleanup on the CONNECTED database (${cleanupAudit.db_name}).\n\nW1 — remove DANGLING references (projects not in your live list) from relevant_projects on ${w1n} target(s); live references are kept.\nW2 — null suggested_pitch_hook / _hooks / bd_angles naming a retired project on ${w2n} target(s).\n\nDerived stale set (retired projects): ${staleList}\nresearch_raw is NOT touched; nulled fields regenerate on the next research pass. Continue?`)) return;
    setCleanupLoading(true);
    setDiagError('');
    try {
      const res = await pipelineApi.cleanupVendorValues(true);
      setCleanupResult(res.data);
    } catch (err) {
      setDiagError(formatApiError(err));
    } finally {
      setCleanupLoading(false);
    }
  };
  usePageTitle('Dashboard');

  useEffect(() => {
    // Check if first visit
    const hasSeenWelcome = localStorage.getItem('breach_welcome_seen');
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }

    const fetchData = async () => {
      try {
        const [statsRes, attentionRes, targetsRes] = await Promise.all([
          dashboardApi.getStats(),
          dashboardApi.getNeedsAttention(),
          targetsApi.getAll(),
        ]);
        setStats(statsRes.data);
        setNeedsAttention(attentionRes.data);
        setRecentTargets(targetsRes.data.slice(0, 5));
        // Fetch grant digest
        try {
          const digestRes = await grantsApi.getDigest();
          setGrantDigest(digestRes.data);
        } catch {}
        // Fetch team activity
        try {
          const teamRes = await dashboardApi.getTeamActivity();
          setTeamActivity(teamRes.data);
        } catch {}
        // Fetch relationship health
        try {
          const healthRes = await dashboardApi.getRelationshipHealth();
          setRelHealth(healthRes.data);
        } catch {}
      } catch (err) {
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCloseWelcome = () => {
    localStorage.setItem('breach_welcome_seen', 'true');
    setShowWelcome(false);
  };

  const handleGenerateSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await dashboardApi.getWeeklySummary();
      setSummary(res.data);
      setSummaryExpanded(true);
    } catch (err) {
      console.error('Summary error:', err);
    } finally {
      setSummaryLoading(false);
    }
  };

  const formatSummaryAsEmail = () => {
    // S6: use the server-rendered plain-text email (owner-first, no markdown/JSON).
    return summary?.email_text || '';
  };

  const handleCopyEmail = async () => {
    const text = formatSummaryAsEmail();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Follow-Up Engine handlers
  const handleGenerateReEngagement = async (record) => {
    setReEngageLoading(record.id);
    setReEngageDraft(null);
    setDraftSaved(false);
    try {
      const res = await dashboardApi.generateReEngagement(record.id);
      setReEngageDraft(res.data);
      setReEngageEditing(res.data.draft_text);
    } catch (err) {
      console.error('Re-engagement error:', err);
    } finally {
      setReEngageLoading(null);
    }
  };

  const handleSaveReEngageDraft = async () => {
    if (!reEngageDraft) return;
    setSavingDraft(true);
    try {
      await outreachApi.saveDraft({
        pipeline_record_id: reEngageDraft.pipeline_record_id,
        outreach_type: 're_engagement',
        draft_text: reEngageEditing,
      });
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch (err) {
      console.error('Save draft error:', err);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleCloseReEngage = () => {
    setReEngageDraft(null);
    setReEngageEditing('');
    setDraftSaved(false);
  };

  // Quick Add handlers
  const handleQuickAdd = async () => {
    if (!qaInput.trim()) return;
    try {
      const res = await dashboardApi.quickAdd(qaInput.trim(), qaBdStream);
      setQaJobId(res.data.job_id);
    } catch (err) {
      console.error('Quick add error:', err);
    }
  };

  useEffect(() => {
    if (!qaJobId) return;
    let cancelled = false;
    pollJob({
      fetchStatus: () => dashboardApi.quickAddStatus(qaJobId).then(r => r.data),
      isComplete: (d) => d.status === 'complete',
      isError: (d) => d.status === 'error' || d.status === 'expired',
      getError: (d) => d.error || 'Quick add failed',
      onProgress: (d) => { if (!cancelled) setQaStatus(d); },
      onComplete: (d) => { if (!cancelled) setQaStatus(d); },
      onError: (msg) => { if (!cancelled) setQaStatus({ status: 'error', error: msg }); },
      intervalMs: 2000,
    });
    return () => { cancelled = true; };
  }, [qaJobId]);

  const handleQuickAddReset = () => {
    setQaInput('');
    setQaJobId(null);
    setQaStatus(null);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '£0';
    if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `£${(value / 1000).toFixed(0)}k`;
    return `£${value.toLocaleString()}`;
  };

  const statCards = [
    { label: 'Total Targets', value: stats?.total_targets || 0, icon: Target, color: '#00D4C8' },
    { label: 'Pipeline Records', value: stats?.total_pipeline || 0, icon: Users, color: 'white' },
    { label: 'Needs Attention', value: stats?.needs_attention || 0, icon: AlertCircle, color: '#FF6B6B' },
  ];

  const dealCards = [
    { label: 'Pipeline Value', value: formatCurrency(stats?.total_pipeline_value), icon: PoundSterling, color: '#00D4C8' },
    { label: 'Weighted Value', value: formatCurrency(stats?.weighted_pipeline_value), icon: Percent, color: '#6BCBFF' },
    { label: 'Won This Year', value: formatCurrency(stats?.won_this_year), icon: Trophy, color: '#22C55E' },
  ];

  const getTemperatureIcon = (temp) => {
    switch (temp) {
      case 'hot': return <Flame className="w-4 h-4 text-red-400" />;
      case 'warm': return <TrendingUp className="w-4 h-4 text-yellow-400" />;
      case 'cold': return <Snowflake className="w-4 h-4 text-blue-400" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card-surface p-6 shimmer h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Welcome Modal */}
      {showWelcome && <WelcomeModal onClose={handleCloseWelcome} />}

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="font-heading text-3xl text-white" data-testid="dashboard-title">Dashboard</h1>
          <HelpButton />
          {process.env.REACT_APP_DIAGNOSTIC_OWNER_EMAIL && user?.email &&
            user.email.toLowerCase() === process.env.REACT_APP_DIAGNOSTIC_OWNER_EMAIL.toLowerCase() && (
            <button
              onClick={runDealValueDiagnostic}
              className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs hover:bg-amber-500/20 transition-colors"
              title="Temporary diagnostic — deal_value source audit"
              data-testid="deal-value-diagnostic-btn"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Deal-Value Diagnostic (temp)
            </button>
          )}
        </div>
        <p className="text-sm text-[#A3B5C7]/70">BD Intelligence Dashboard</p>
      </div>

      {/* TEMP Deal-Value Diagnostic panel — admin only, remove after review */}
      {diagOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="deal-value-diagnostic-modal">
          <div className="w-full max-w-4xl max-h-[85vh] overflow-y-auto card-surface p-6 rounded-xl border border-amber-500/30">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" /> Diagnostic Panel (temp)
              </h2>
              <button onClick={() => setDiagOpen(false)} className="p-1.5 rounded-lg text-[#A3B5C7] hover:text-white hover:bg-[#1A2F4C]" data-testid="deal-value-diagnostic-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {diagLoading && (
              <div className="flex items-center gap-2 text-[#A3B5C7] py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
            )}
            {diagError && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{diagError}</div>}
            {diagData && !diagLoading && (
              <div className="space-y-5" data-testid="deal-value-diagnostic-content">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400/90 border-b border-amber-500/20 pb-1">① Deal Value</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    ['Total records', diagData.total_records],
                    ['With value (>0)', diagData.records_with_value],
                    ['Zero / missing', diagData.records_zero_or_missing],
                    ['Total deal value', formatCurrency(diagData.total_deal_value)],
                    ['Min non-zero', formatCurrency(diagData.min_non_zero)],
                    ['Max deal value', formatCurrency(diagData.max_deal_value)],
                    ['Average non-zero', formatCurrency(diagData.average_non_zero)],
                  ].map(([label, val]) => (
                    <div key={label} className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                      <p className="text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1">{label}</p>
                      <p className="text-lg font-heading text-white">{val ?? '—'}</p>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-amber-300 mb-2">Largest 10 by deal_value</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-[#A3B5C7] border-b border-[#1A2F4C]">
                          <th className="py-2 pr-3">Target</th>
                          <th className="py-2 pr-3">Deal value</th>
                          <th className="py-2 pr-3">Stage</th>
                          <th className="py-2 pr-3">Owner</th>
                          <th className="py-2 pr-3">Created by</th>
                          <th className="py-2 pr-3">Created at</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(diagData.largest_ten || []).map((r, i) => (
                          <tr key={i} className="border-b border-[#1A2F4C]/50 text-[#D4E2F0]" data-testid={`diag-row-${i}`}>
                            <td className="py-2 pr-3">{r.target_name}</td>
                            <td className="py-2 pr-3 text-[#00D4C8] font-medium">{formatCurrency(r.deal_value)}</td>
                            <td className="py-2 pr-3">{r.stage}</td>
                            <td className="py-2 pr-3">{r.owner}</td>
                            <td className="py-2 pr-3">{r.created_by}</td>
                            <td className="py-2 pr-3">{r.created_at ? String(r.created_at).slice(0, 10) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* WIPE control */}
                <div className="pt-4 border-t border-[#1A2F4C]">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm text-white font-medium">Wipe pre-proposal deal values</p>
                      <p className="text-[11px] text-[#A3B5C7]/80">Nulls deal_value on every non-zero record below Proposal Sent, on the connected DB. Audited.</p>
                    </div>
                    <button
                      onClick={runDealValueWipe}
                      disabled={wipeLoading}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-500/50 bg-red-500/10 text-red-300 text-xs hover:bg-red-500/20 transition-colors disabled:opacity-50"
                      data-testid="deal-value-wipe-btn"
                    >
                      {wipeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                      Run Wipe
                    </button>
                  </div>
                  {wipeResult && (
                    <div className="mt-3 p-3 rounded-lg bg-[#0A1628] border border-green-500/30 text-xs text-[#D4E2F0]" data-testid="deal-value-wipe-result">
                      <p>Wrote to DB: <span className="text-[#00D4C8] font-medium">{wipeResult.db_name}</span> · total records: <span className="font-medium">{wipeResult.total_records}</span></p>
                      <p className="mt-1">Non-zero before: <span className="font-medium">{wipeResult.before_nonzero}</span> → wiped: <span className="text-red-300 font-medium">{wipeResult.wiped}</span> → non-zero after: <span className="text-green-400 font-medium">{wipeResult.after_nonzero}</span></p>
                      {(wipeResult.details || []).length > 0 && (
                        <ul className="mt-2 space-y-0.5">
                          {wipeResult.details.map((d, i) => (
                            <li key={i} className="text-[#A3B5C7]">• {d.target_name} ({d.stage}) — cleared {formatCurrency(d.old_deal_value)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                {/* FIELD AUDIT (owners & categories) — read-only */}
                <div className="pt-4 border-t border-[#1A2F4C]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400/90 border-b border-amber-500/20 pb-1 mb-3">② Owners</p>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm text-white font-medium">Field audit — owners & pipeline categories</p>
                      <p className="text-[11px] text-[#A3B5C7]/80">Read-only distributions from the connected DB. No changes made.</p>
                    </div>
                    <button
                      onClick={runFieldAudit}
                      disabled={fieldAuditLoading}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#00D4C8]/40 bg-[#00D4C8]/10 text-[#00D4C8] text-xs hover:bg-[#00D4C8]/20 transition-colors disabled:opacity-50"
                      data-testid="field-audit-btn"
                    >
                      {fieldAuditLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      Load Field Audit
                    </button>
                  </div>
                  {fieldAudit && (
                    <div className="mt-3 grid md:grid-cols-2 gap-4 text-xs" data-testid="field-audit-result">
                      <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                        <p className="text-amber-300 uppercase tracking-wider text-[10px] mb-2">Owner values → canonical ({fieldAudit.db_name}, {fieldAudit.total_targets} targets / {fieldAudit.total_pipeline} pipeline)</p>
                        {(fieldAudit.ambiguous_owners || []).length > 0 && (
                          <div className="mb-2 p-2 rounded bg-red-500/10 border border-red-500/30" data-testid="ambiguous-owners-warning">
                            <p className="text-red-300 text-[10px] uppercase tracking-wider mb-1">⚠ Ambiguous owners → Unassigned</p>
                            {(fieldAudit.ambiguous_owners || []).map((a, i) => (
                              <p key={i} className="text-red-200 text-[11px]">“{a.raw}” ({a.count}) matches {a.matched.join(', ')}</p>
                            ))}
                          </div>
                        )}
                        <table className="w-full">
                          <thead><tr className="text-left text-[#A3B5C7]"><th className="pb-1">Raw value</th><th className="pb-1">Count</th><th className="pb-1">→ Canonical</th></tr></thead>
                          <tbody>
                            {(fieldAudit.owner_canonical_map || []).map((o, i) => (
                              <tr key={i} className={`text-[#D4E2F0] ${o.status === 'ambiguous' ? 'bg-red-500/10' : ''}`}><td className="py-0.5">{o.raw}</td><td className="py-0.5">{o.count}</td><td className={`py-0.5 ${o.status === 'ambiguous' ? 'text-red-300' : 'text-[#00D4C8]'}`}>{o.canonical}{o.status === 'ambiguous' ? ' ⚠' : ''}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                        <p className="text-amber-300 uppercase tracking-wider text-[10px] mb-2">business_line distribution</p>
                        <ul className="space-y-0.5 mb-3">
                          {Object.entries(fieldAudit.business_line_distribution || {}).map(([k, v]) => (
                            <li key={k} className="text-[#D4E2F0]">{k} — <span className="font-medium">{v}</span></li>
                          ))}
                        </ul>
                        <p className="text-amber-300 uppercase tracking-wider text-[10px] mb-2">project_category distribution</p>
                        <ul className="space-y-0.5">
                          {Object.entries(fieldAudit.project_category_distribution || {}).map(([k, v]) => (
                            <li key={k} className="text-[#D4E2F0]">{k} — <span className="font-medium">{v}</span></li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* OWNER MIGRATION */}
                  <div className="mt-4 pt-4 border-t border-[#1A2F4C]">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm text-white font-medium">Migrate owners → canonical full names</p>
                        <p className="text-[11px] text-[#A3B5C7]/80">Rewrites every target &amp; pipeline record owner on the connected DB. Audited.</p>
                      </div>
                      <button
                        onClick={runOwnerMigration}
                        disabled={ownerMigrateLoading}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-300 text-xs hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                        data-testid="owner-migrate-btn"
                      >
                        {ownerMigrateLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                        Run Owner Migration
                      </button>
                    </div>
                    {ownerMigrateResult && (
                      <div className="mt-3 p-3 rounded-lg bg-[#0A1628] border border-green-500/30 text-xs text-[#D4E2F0]" data-testid="owner-migrate-result">
                        <p>Wrote to DB: <span className="text-[#00D4C8] font-medium">{ownerMigrateResult.db_name}</span></p>
                        <p className="mt-1">Targets changed: <span className="font-medium">{ownerMigrateResult.changed_targets}</span> · Pipeline changed: <span className="font-medium">{ownerMigrateResult.changed_pipeline}</span></p>
                        <p className="mt-1">Distinct owners now (targets): <span className="text-green-400">{(ownerMigrateResult.distinct_target_owners || []).join(', ')}</span></p>
                        <p className="mt-1">Distinct owners now (pipeline): <span className="text-green-400">{(ownerMigrateResult.distinct_pipeline_owners || []).join(', ')}</span></p>
                      </div>
                    )}
                  </div>
                </div>

                {/* ③ CATEGORIES — audit-first migration (apply only on approval) */}
                <div className="pt-4 border-t border-[#1A2F4C]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400/90 border-b border-amber-500/20 pb-1 mb-3">③ Categories</p>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm text-white font-medium">Category migration — audit &amp; consolidate</p>
                      <p className="text-[11px] text-[#A3B5C7]/80">Read-only audit first: legacy business_line / project_category counts + a proposed mapping to the six categories. Nothing is written until you approve.</p>
                    </div>
                    <button
                      onClick={runCategoryAudit}
                      disabled={catAuditLoading}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#00D4C8]/40 bg-[#00D4C8]/10 text-[#00D4C8] text-xs hover:bg-[#00D4C8]/20 transition-colors disabled:opacity-50"
                      data-testid="category-audit-btn"
                    >
                      {catAuditLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      Run Category Audit
                    </button>
                  </div>
                  {catAudit && (
                    <div className="mt-3 grid md:grid-cols-2 gap-4 text-xs" data-testid="category-audit-result">
                      <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                        <p className="text-amber-300 uppercase tracking-wider text-[10px] mb-2">Effective project_category ({catAudit.db_name}, {catAudit.total_pipeline} records)</p>
                        <ul className="space-y-0.5 mb-3">
                          {Object.entries(catAudit.effective_project_category_distribution || {}).map(([k, v]) => (
                            <li key={k} className="text-[#D4E2F0]">{k} — <span className="font-medium">{v}</span></li>
                          ))}
                        </ul>
                        <p className="text-amber-300 uppercase tracking-wider text-[10px] mb-2">business_line distribution</p>
                        <ul className="space-y-0.5">
                          {Object.entries(catAudit.business_line_distribution || {}).map(([k, v]) => (
                            <li key={k} className="text-[#D4E2F0]">{k} — <span className="font-medium">{v}</span></li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                        <p className="text-amber-300 uppercase tracking-wider text-[10px] mb-2">Proposed mapping (legacy → new category)</p>
                        <table className="w-full">
                          <tbody>
                            {Object.entries(catAudit.proposed_mapping || {}).map(([k, v]) => (
                              <tr key={k} className="text-[#D4E2F0]"><td className="py-0.5 pr-2">{k}</td><td className="py-0.5 text-[#00D4C8]">→ {v}</td></tr>
                            ))}
                            {Object.keys(catAudit.proposed_mapping || {}).length === 0 && (
                              <tr><td className="py-0.5 text-[#A3B5C7]">No legacy values found — nothing to remap.</td></tr>
                            )}
                          </tbody>
                        </table>
                        <p className="text-[10px] text-[#A3B5C7]/70 mt-2">Anything unmapped falls to "Other / Unclassified".</p>
                      </div>
                    </div>
                  )}
                  {catAudit && (
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="text-[11px] text-[#A3B5C7]/80">Applying writes `category` on every record and removes the dead `business_line` on the connected DB. Audited.</p>
                      <button
                        onClick={runCategoryMigration}
                        disabled={catMigrateLoading}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-300 text-xs hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                        data-testid="category-migrate-btn"
                      >
                        {catMigrateLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                        Apply Proposed Mapping
                      </button>
                    </div>
                  )}
                  {catMigrateResult && (
                    <div className="mt-3 p-3 rounded-lg bg-[#0A1628] border border-green-500/30 text-xs text-[#D4E2F0]" data-testid="category-migrate-result">
                      <p>Wrote to DB: <span className="text-[#00D4C8] font-medium">{catMigrateResult.db_name}</span> · records changed: <span className="font-medium">{catMigrateResult.records_changed}</span></p>
                      <p className="mt-1">Resulting categories: <span className="text-green-400">{Object.entries(catMigrateResult.resulting_category_distribution || {}).map(([k, v]) => `${k} (${v})`).join(', ')}</span></p>
                    </div>
                  )}
                </div>

                {/* ④ SCHEMA KEYS — audit-first legacy-vendor-key migration (apply only on approval) */}
                <div className="pt-4 border-t border-[#1A2F4C]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400/90 border-b border-amber-500/20 pb-1 mb-3">④ Schema Keys</p>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm text-white font-medium">Schema-key migration — legacy vendor keys</p>
                      <p className="text-[11px] text-[#A3B5C7]/80">Read-only audit first: counts of records carrying legacy schema keys + the rename plan. Nothing is written until you approve.</p>
                    </div>
                    <button
                      onClick={runSchemaAudit}
                      disabled={schemaAuditLoading}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#00D4C8]/40 bg-[#00D4C8]/10 text-[#00D4C8] text-xs hover:bg-[#00D4C8]/20 transition-colors disabled:opacity-50"
                      data-testid="schema-audit-btn"
                    >
                      {schemaAuditLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      Run Schema Audit
                    </button>
                  </div>
                  {schemaAudit && (
                    <div className="mt-3 grid md:grid-cols-2 gap-4 text-xs" data-testid="schema-audit-result">
                      <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                        <p className="text-amber-300 uppercase tracking-wider text-[10px] mb-2">Records carrying legacy keys ({schemaAudit.db_name})</p>
                        <ul className="space-y-0.5">
                          <li className="text-[#D4E2F0]">Targets (in_depth_fit_assessment) — <span className="font-medium">{schemaAudit.target_count}</span></li>
                          <li className="text-[#D4E2F0]">Briefings (intelligence_suite_fit) — <span className="font-medium">{schemaAudit.briefing_count}</span></li>
                        </ul>
                      </div>
                      <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                        <p className="text-amber-300 uppercase tracking-wider text-[10px] mb-2">Planned key renames</p>
                        <table className="w-full">
                          <tbody>
                            <tr className="text-[#D4E2F0]"><td className="py-0.5 pr-2">in_depth_fit_assessment</td><td className="py-0.5 text-[#00D4C8]">→ fit_assessment</td></tr>
                            <tr className="text-[#D4E2F0]"><td className="py-0.5 pr-2">intelligence_suite_fit</td><td className="py-0.5 text-[#00D4C8]">→ offering_fit</td></tr>
                          </tbody>
                        </table>
                        <p className="text-[10px] text-[#A3B5C7]/70 mt-2">Renderers dual-read (new key first, legacy fallback), so the UI is unchanged before/after.</p>
                      </div>
                    </div>
                  )}
                  {schemaAudit && (
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="text-[11px] text-[#A3B5C7]/80">Applying renames the two legacy keys on the connected DB ({schemaAudit.target_count} targets, {schemaAudit.briefing_count} briefings). Audited.</p>
                      <button
                        onClick={runSchemaMigration}
                        disabled={schemaMigrateLoading}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-300 text-xs hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                        data-testid="schema-migrate-btn"
                      >
                        {schemaMigrateLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                        Apply Migration
                      </button>
                    </div>
                  )}
                  {schemaMigrateResult && (
                    <div className="mt-3 p-3 rounded-lg bg-[#0A1628] border border-green-500/30 text-xs text-[#D4E2F0]" data-testid="schema-migrate-result">
                      <p>Wrote to DB: <span className="text-[#00D4C8] font-medium">{schemaMigrateResult.db_name}</span> · targets migrated: <span className="font-medium">{schemaMigrateResult.targets_migrated}</span> · briefings migrated: <span className="font-medium">{schemaMigrateResult.briefings_migrated}</span></p>
                    </div>
                  )}
                </div>

                {/* ⑤ DATA CLEANUP — vendor names baked into target output values (audit-first) */}
                <div className="pt-4 border-t border-[#1A2F4C]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400/90 border-b border-amber-500/20 pb-1 mb-3">⑤ Data Cleanup</p>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm text-white font-medium">Dangling project references — targets pointing at projects that no longer exist</p>
                      <p className="text-[11px] text-[#A3B5C7]/80">Read-only audit first. The stale set is derived from your LIVE projects collection — live project names are never flagged. W1 removes only dangling entries from relevant_projects (live refs kept); W2 nulls pitch/angle text naming a retired project. research_raw is not touched. Nothing is written until you approve.</p>
                    </div>
                    <button
                      onClick={runCleanupAudit}
                      disabled={cleanupAuditLoading}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#00D4C8]/40 bg-[#00D4C8]/10 text-[#00D4C8] text-xs hover:bg-[#00D4C8]/20 transition-colors disabled:opacity-50"
                      data-testid="cleanup-audit-btn"
                    >
                      {cleanupAuditLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      Run Cleanup Audit
                    </button>
                  </div>
                  {cleanupAudit && (
                    <>
                    <div className="mt-3 p-2 rounded bg-[#0A1628] border border-[#1A2F4C] text-[11px]" data-testid="cleanup-stale-set">
                      <span className="text-amber-300 uppercase tracking-wider text-[10px]">Derived stale set (retired projects): </span>
                      <span className="text-[#D4E2F0]">{(cleanupAudit.stale_set || []).length ? (cleanupAudit.stale_set || []).join(', ') : 'none'}</span>
                      <span className="text-[#A3B5C7]/60"> · live projects kept: {(cleanupAudit.live_projects || []).length}</span>
                    </div>
                    <div className="mt-3 grid md:grid-cols-2 gap-4 text-xs" data-testid="cleanup-audit-result">
                      <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                        <p className="text-amber-300 uppercase tracking-wider text-[10px] mb-2">W1 — dangling refs removed, live kept ({cleanupAudit.db_name})</p>
                        <p className="text-[#D4E2F0] mb-1">Affected targets: <span className="font-medium">{cleanupAudit.w1_relevant_projects?.count}</span></p>
                        <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                          {(cleanupAudit.w1_relevant_projects?.targets || []).map((t, i) => (
                            <li key={i} className="text-[#A3B5C7]">• {t.name} <span className="text-red-300/80">− [{(t.removed || []).join(', ')}]</span></li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                        <p className="text-amber-300 uppercase tracking-wider text-[10px] mb-2">W2 — null suggestion fields naming a retired project</p>
                        <p className="text-[#D4E2F0] mb-1">Affected targets: <span className="font-medium">{cleanupAudit.w2_suggestion_text?.count}</span></p>
                        <p className="text-[#A3B5C7] text-[11px] mb-1">Per field: {Object.entries(cleanupAudit.w2_suggestion_text?.field_counts || {}).map(([k, v]) => `${k} (${v})`).join(', ')}</p>
                        <ul className="space-y-0.5 max-h-24 overflow-y-auto">
                          {(cleanupAudit.w2_suggestion_text?.targets || []).map((t, i) => (
                            <li key={i} className="text-[#A3B5C7]">• {t.name} [{(t.fields || []).join(', ')}]</li>
                          ))}
                        </ul>
                        <p className="text-[10px] text-[#A3B5C7]/70 mt-2">W3 research_raw: <span className="text-[#A3B5C7]">SKIPPED (not touched)</span>. Nulled fields regenerate on next research pass.</p>
                      </div>
                    </div>
                    </>
                  )}
                  {cleanupAudit && (
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="text-[11px] text-[#A3B5C7]/80">Applies W1 ({cleanupAudit.w1_relevant_projects?.count} targets) + W2 ({cleanupAudit.w2_suggestion_text?.count} targets) on the connected DB. Audited.</p>
                      <button
                        onClick={runCleanupApply}
                        disabled={cleanupLoading}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-300 text-xs hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                        data-testid="cleanup-apply-btn"
                      >
                        {cleanupLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                        Apply Cleanup
                      </button>
                    </div>
                  )}
                  {cleanupResult && (
                    <div className="mt-3 p-3 rounded-lg bg-[#0A1628] border border-green-500/30 text-xs text-[#D4E2F0]" data-testid="cleanup-apply-result">
                      <p>Wrote to DB: <span className="text-[#00D4C8] font-medium">{cleanupResult.db_name}</span> · targets with dangling refs removed: <span className="font-medium">{cleanupResult.w1_relevant_projects?.migrated}</span> · suggestion records nulled: <span className="font-medium">{cleanupResult.w2_suggestion_text?.migrated}</span> · research_raw: untouched</p>
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Add Bar — hidden for Viewer */}
      <WriteOnly>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-surface p-5 mb-8"
        data-testid="quick-add-bar"
      >
        {!qaJobId ? (
          <div className="flex items-center gap-3">
            <div className="flex-1 flex items-center gap-3 px-4 py-2.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] focus-within:border-[#00D4C8]/40 transition-colors">
              <Search className="w-4 h-4 text-[#A3B5C7]" />
              <input
                value={qaInput}
                onChange={(e) => setQaInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAdd(); }}
                placeholder="Add a prospect — type a company, person, or URL..."
                className="flex-1 bg-transparent text-white text-sm placeholder:text-[#A3B5C7]/50 focus:outline-none"
                data-testid="quick-add-input"
              />
            </div>
            <div className="relative">
              <button
                onClick={() => setQaShowStreams(!qaShowStreams)}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-xs text-[#A3B5C7] hover:text-white transition-colors whitespace-nowrap"
                data-testid="quick-add-stream-selector"
              >
                {qaBdStream}
                <ChevronDown className="w-3 h-3" />
              </button>
              {qaShowStreams && (
                <div className="absolute top-full right-0 mt-1 w-52 bg-[#0E1C33] border border-[#1A2F4C] rounded-lg shadow-xl z-10 py-1">
                  {BD_STREAMS.map((s) => (
                    <button
                      key={s}
                      onClick={() => { setQaBdStream(s); setQaShowStreams(false); }}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors ${s === qaBdStream ? 'text-[#00D4C8] bg-[#00D4C8]/10' : 'text-[#A3B5C7] hover:bg-[#0A1628] hover:text-white'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleQuickAdd}
              disabled={!qaInput.trim()}
              className="btn-primary flex items-center gap-2 whitespace-nowrap"
              data-testid="quick-add-launch-btn"
            >
              <Zap className="w-4 h-4" /> Launch BREACH on...
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-[#00D4C8]" />
              <span className="text-sm text-white font-medium">BREACHing {qaInput}...</span>
            </div>
            {/* Progress Steps */}
            <div className="flex items-center gap-6">
              {QUICK_ADD_STEPS.map((step, i) => {
                const stepNum = i + 1;
                const isDone = (qaStatus?.step || 0) > stepNum || qaStatus?.status === 'complete';
                const isActive = (qaStatus?.step || 0) === stepNum && qaStatus?.status !== 'complete';
                return (
                  <div key={step.key} className="flex items-center gap-2" data-testid={`qa-step-${step.key}`}>
                    {isDone ? (
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center"><Check className="w-3 h-3 text-green-400" /></div>
                    ) : isActive ? (
                      <div className="w-5 h-5 rounded-full bg-[#00D4C8]/20 flex items-center justify-center"><Loader2 className="w-3 h-3 text-[#00D4C8] animate-spin" /></div>
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-[#1A2F4C] flex items-center justify-center"><span className="text-[10px] text-[#A3B5C7]">{stepNum}</span></div>
                    )}
                    <span className={`text-xs ${isDone ? 'text-green-400' : isActive ? 'text-white' : 'text-[#A3B5C7]/50'}`}>{step.label}</span>
                  </div>
                );
              })}
            </div>
            {/* Result Preview */}
            {qaStatus?.status === 'complete' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 pt-2 border-t border-[#1A2F4C]">
                {qaStatus.research?.summary && (
                  <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                    <p className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1 font-medium">Research Summary</p>
                    <p className="text-sm text-[#D4E2F0] line-clamp-2">{qaStatus.research.summary}</p>
                  </div>
                )}
                {qaStatus.outreach && (
                  <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                    <p className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1 font-medium">Outreach Draft</p>
                    <p className="text-sm text-[#D4E2F0] line-clamp-3 whitespace-pre-line">{qaStatus.outreach}</p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Link to={`/targets/${qaStatus.target_id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#00D4C8]/10 text-[#00D4C8] text-xs hover:bg-[#00D4C8]/20 transition-colors" data-testid="qa-view-research">
                    Research <ArrowRight className="w-3 h-3" />
                  </Link>
                  <Link to="/pipeline" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#00D4C8]/10 text-[#00D4C8] text-xs hover:bg-[#00D4C8]/20 transition-colors">
                    Pipeline <ArrowRight className="w-3 h-3" />
                  </Link>
                  <Link to="/outreach" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#00D4C8]/10 text-[#00D4C8] text-xs hover:bg-[#00D4C8]/20 transition-colors">
                    Outreach <ArrowRight className="w-3 h-3" />
                  </Link>
                  <button onClick={handleQuickAddReset} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#1A2F4C] text-[#A3B5C7] text-xs hover:text-white transition-colors" data-testid="qa-add-another">
                    <Zap className="w-3 h-3" /> Add Another
                  </button>
                </div>
              </motion.div>
            )}
            {qaStatus?.status === 'error' && (
              <p className="text-red-400 text-sm">{qaStatus.error || 'Something went wrong'}</p>
            )}
          </div>
        )}
      </motion.div>
      </WriteOnly>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card-surface p-6"
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-1">{stat.label}</p>
                <p className="text-4xl font-heading text-[white]">{stat.value}</p>
              </div>
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}15` }}
              >
                <stat.icon className="w-6 h-6" style={{ color: stat.color }} strokeWidth={1.5} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Deal Value Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-testid="deal-value-stats">
        {dealCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + index * 0.1 }}
            className="card-surface p-6 border-t-2"
            style={{ borderTopColor: stat.color }}
            data-testid={`deal-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#A3B5C7] mb-1">{stat.label}</p>
                <p className="text-3xl font-heading text-white">{stat.value}</p>
              </div>
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}15` }}
              >
                <stat.icon className="w-5 h-5" style={{ color: stat.color }} strokeWidth={1.5} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Team Activity */}
      {teamActivity.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-8"
          data-testid="team-activity-section"
        >
          <h2 className="font-heading text-xl text-white flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-[#00D4C8]" strokeWidth={1.5} />
            Team Activity
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {teamActivity.map((member) => {
              const total = member.active + member.won + member.stalled + member.cold;
              return (
                <div
                  key={member.user_id}
                  className="card-surface p-5 hover:border-[#00D4C8]/30 transition-all cursor-pointer"
                  onClick={() => navigate(`/targets?owner=${encodeURIComponent(member.user_name)}`)}
                  data-testid={`team-card-${member.user_name.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#00D4C8]/10 flex items-center justify-center">
                      <UserCircle className="w-5 h-5 text-[#00D4C8]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{member.user_name}</p>
                      <p className="text-[10px] text-[#A3B5C7] uppercase tracking-wider">{member.email.split('@')[0]}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-2.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-center">
                      <p className="text-lg font-heading text-white">{member.targets_count}</p>
                      <p className="text-[9px] uppercase tracking-wider text-[#A3B5C7]">Targets</p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-center">
                      <p className="text-lg font-heading text-white">{member.pipeline_count}</p>
                      <p className="text-[9px] uppercase tracking-wider text-[#A3B5C7]">Pipeline</p>
                    </div>
                  </div>

                  {total > 0 ? (
                    <div className="space-y-2">
                      {[
                        { label: 'Active', count: member.active, color: '#00D4C8', onClick: () => navigate(`/targets?owner=${encodeURIComponent(member.user_name)}&status=active`) },
                        { label: 'Won', count: member.won, color: '#22C55E', onClick: () => navigate(`/targets?owner=${encodeURIComponent(member.user_name)}&status=won`) },
                        { label: 'Stalled', count: member.stalled, color: '#FFD93D', onClick: () => navigate(`/targets?owner=${encodeURIComponent(member.user_name)}&status=stalled`) },
                        { label: 'Cold', count: member.cold, color: '#6BCBFF', onClick: () => navigate(`/targets?owner=${encodeURIComponent(member.user_name)}&status=cold`) },
                      ].map((item) => (
                        <button
                          key={item.label}
                          onClick={(e) => { e.stopPropagation(); item.onClick(); }}
                          className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors group"
                          data-testid={`team-${member.user_name.toLowerCase()}-${item.label.toLowerCase()}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                            <span className="text-xs text-[#A3B5C7] group-hover:text-white transition-colors">{item.label}</span>
                          </div>
                          <span className="text-xs font-medium" style={{ color: item.color }}>{item.count}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-[#A3B5C7] text-center py-2">No pipeline records</p>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Relationship Health Panel */}
      {relHealth && (relHealth.warmest?.length > 0 || relHealth.needs_attention?.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-8"
          data-testid="relationship-health-section"
        >
          <h2 className="font-heading text-xl text-white flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-[#00D4C8]" strokeWidth={1.5} />
            Relationship Health
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Top 5 Warmest */}
            <div className="card-surface p-5">
              <h3 className="text-xs uppercase tracking-wider text-[#00D4C8] font-medium mb-3">Top 5 Warmest</h3>
              <div className="space-y-2">
                {(relHealth.warmest || []).map((r) => {
                  const sc = getScoreColor(r.score);
                  return (
                    <div key={r.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm text-white truncate">{r.target_name}</span>
                        <span className="text-[10px] text-[#A3B5C7] uppercase">{r.stage?.replace('_', ' ')}</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${sc.bg} ${sc.text} border ${sc.border}`} data-testid={`score-${r.id}`}>
                        {r.score}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Top 3 Needs Attention */}
            <div className="card-surface p-5">
              <h3 className="text-xs uppercase tracking-wider text-[#FF6B6B] font-medium mb-3 flex items-center gap-2">
                <ShieldAlert className="w-3.5 h-3.5" /> Needs Attention
              </h3>
              <div className="space-y-2">
                {(relHealth.needs_attention || []).length > 0 ? (relHealth.needs_attention || []).map((r) => {
                  const sc = getScoreColor(r.score);
                  return (
                    <div key={r.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm text-white truncate">{r.target_name}</span>
                        <span className="text-[10px] text-[#A3B5C7] uppercase">{r.stage?.replace('_', ' ')}</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${sc.bg} ${sc.text} border ${sc.border}`}>
                        {r.score}
                      </span>
                    </div>
                  );
                }) : (
                  <p className="text-xs text-[#A3B5C7] text-center py-2">All relationships healthy</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Weekly Summary Section */}
      <div className="mb-8" data-testid="weekly-summary-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#00D4C8]" strokeWidth={1.5} />
            Weekly BD Summary
          </h2>
          <div className="flex items-center gap-2">
            {summary && (
              <button
                onClick={handleCopyEmail}
                className="btn-secondary flex items-center gap-2 text-xs"
                data-testid="copy-summary-email-btn"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied' : 'Copy as Email'}
              </button>
            )}
            <button
              onClick={handleGenerateSummary}
              disabled={summaryLoading}
              className="btn-primary flex items-center gap-2 text-sm"
              data-testid="generate-summary-btn"
            >
              {summaryLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Zap className="w-4 h-4" strokeWidth={2} />
              )}
              {summaryLoading ? 'Generating...' : 'Generate Summary'}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {summary && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="card-surface"
            >
              {/* Summary header */}
              <button
                onClick={() => setSummaryExpanded(!summaryExpanded)}
                className="w-full p-6 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#A3B5C7] uppercase tracking-wider">
                    {summary.period?.label || new Date(summary.generated_at).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </span>
                  <span className="text-xs text-[#00D4C8]">
                    {(summary.people?.length || 0)} {summary.people?.length === 1 ? 'person' : 'people'} · {summary.totals?.active_deals || 0} active deals
                  </span>
                </div>
                {summaryExpanded ? <ChevronUp className="w-4 h-4 text-[#A3B5C7]" /> : <ChevronDown className="w-4 h-4 text-[#A3B5C7]" />}
              </button>

              {summaryExpanded && (
                <div className="px-6 pb-6 space-y-5" data-testid="summary-people">
                  {(summary.people || []).length === 0 && (
                    <p className="text-sm text-[#A3B5C7]">No BD activity recorded this week.</p>
                  )}
                  {(summary.people || []).map((p, i) => (
                    <div key={i} className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]" data-testid={`summary-person-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-heading text-white">{p.owner}</h4>
                        <span className="text-[10px] uppercase tracking-wider text-[#A3B5C7]">{p.movement_summary}</span>
                      </div>
                      <p className="text-sm text-[#D4E2F0] italic border-l-2 border-[#00D4C8] pl-3 mb-3">{p.headline}</p>
                      <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
                        {p.advanced > 0 && <span className="px-2 py-0.5 rounded bg-[#00D4C8]/10 text-[#00D4C8]">{p.advanced} advanced</span>}
                        {p.new_proposals > 0 && <span className="px-2 py-0.5 rounded bg-[#00D4C8]/10 text-[#00D4C8]">{p.new_proposals} new proposal(s)</span>}
                        {p.churned > 0 && <span className="px-2 py-0.5 rounded bg-[#FF6B6B]/10 text-[#FF6B6B]">{p.churned} churned</span>}
                        {(p.new_targets?.length || 0) > 0 && <span className="px-2 py-0.5 rounded bg-white/10 text-[#D4E2F0]">{p.new_targets.length} new target(s)</span>}
                        <span className="px-2 py-0.5 rounded bg-white/10 text-[#D4E2F0]" data-testid={`summary-outreach-${i}`}>
                          {p.outreach?.sent || 0} sent / {p.outreach?.drafted || 0} drafted
                          {p.outreach?.sent_by_channel && Object.keys(p.outreach.sent_by_channel).length ? ` (${Object.entries(p.outreach.sent_by_channel).map(([k, v]) => `${k}: ${v}`).join(', ')})` : ''}
                        </span>
                      </div>
                      {(p.stale_leads?.length || 0) > 0 && (
                        <div className="mb-2" data-testid={`summary-stale-${i}`}>
                          <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF6B6B] mb-1">Stale leads (worst first)</p>
                          <div className="space-y-1">
                            {p.stale_leads.slice(0, 10).map((s, j) => (
                              <div key={j} className="flex items-center justify-between text-xs">
                                <span className="text-white">{s.target_name} <span className="text-[#A3B5C7]">({s.stage})</span></span>
                                <span className="text-[#FF6B6B]">{s.age_label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {(p.suggestions?.length || 0) > 0 && (
                        <div className="pt-2 border-t border-[#1A2F4C]">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-[#00D4C8] mb-1">Suggested (not applied)</p>
                          {p.suggestions.map((s, k) => <p key={k} className="text-xs text-[#D4E2F0]">• {s}</p>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Needs Attention — Follow-Up Engine */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card-surface p-6"
          data-testid="needs-attention-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl text-[white]">Needs Attention</h2>
            <span className="text-xs uppercase tracking-[0.15em] text-[#00D4C8]">
              &gt;14 days since contact
            </span>
          </div>
          
          {needsAttention.length === 0 ? (
            <p className="text-[#A3B5C7] text-sm py-8 text-center">
              All caught up! No dormant leads.
            </p>
          ) : (
            <div className="space-y-3">
              {needsAttention.slice(0, 5).map((record) => (
                <div
                  key={record.id}
                  className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C] hover:border-[#00D4C8]/30 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getTemperatureIcon(record.temperature)}
                      <div>
                        <p className="text-sm text-[white]">{record.target_name || 'Unknown Target'}</p>
                        <p className="text-xs text-[#A3B5C7]">{record.stage} {record.contact_name ? `— ${record.contact_name}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleGenerateReEngagement(record)}
                        disabled={reEngageLoading === record.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[#00D4C8]/10 text-[#00D4C8] border border-[#00D4C8]/20 hover:bg-[#00D4C8]/20 transition-colors disabled:opacity-50"
                        data-testid={`re-engage-btn-${record.id}`}
                      >
                        {reEngageLoading === record.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                        )}
                        Re-engage
                      </button>
                      <Link
                        to={`/pipeline`}
                        className="text-[#A3B5C7] hover:text-[#00D4C8] transition-colors"
                      >
                        <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Re-engagement Draft Modal */}
          <AnimatePresence>
            {reEngageDraft && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 overflow-hidden"
              >
                <div className="p-5 rounded-xl bg-[#0E1C33] border border-[#00D4C8]/30" data-testid="re-engage-draft-panel">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-[#00D4C8]" strokeWidth={1.5} />
                      <h4 className="text-sm font-medium text-white">
                        Re-engagement: {reEngageDraft.target_name}
                      </h4>
                      {reEngageDraft.days_since_contact && (
                        <span className="text-[10px] px-2 py-0.5 bg-red-500/10 text-red-400 rounded border border-red-500/20">
                          {reEngageDraft.days_since_contact}d since contact
                        </span>
                      )}
                    </div>
                    <button onClick={handleCloseReEngage} className="text-[#A3B5C7] hover:text-white">
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  <textarea
                    value={reEngageEditing}
                    onChange={(e) => setReEngageEditing(e.target.value)}
                    rows={8}
                    className="w-full p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[#D4E2F0] text-sm leading-relaxed focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 transition-colors resize-none"
                    data-testid="re-engage-draft-textarea"
                  />
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-[#A3B5C7] flex items-center gap-1">
                      <Edit3 className="w-3 h-3" strokeWidth={1.5} />
                      Edit before saving
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(reEngageEditing);
                          } catch {
                            const ta = document.createElement('textarea');
                            ta.value = reEngageEditing;
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                          }
                        }}
                        className="btn-secondary flex items-center gap-1.5 text-xs"
                      >
                        <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Copy
                      </button>
                      <button
                        onClick={handleSaveReEngageDraft}
                        disabled={savingDraft || draftSaved}
                        className="btn-primary flex items-center gap-1.5 text-xs"
                        data-testid="save-re-engage-draft-btn"
                      >
                        {savingDraft ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : draftSaved ? (
                          <Check className="w-3.5 h-3.5" strokeWidth={2} />
                        ) : (
                          <Send className="w-3.5 h-3.5" strokeWidth={1.5} />
                        )}
                        {savingDraft ? 'Saving...' : draftSaved ? 'Saved to Outreach' : 'Save Draft'}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Recent Targets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-surface p-6"
          data-testid="recent-targets-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl text-[white]">Recent Intelligence</h2>
            <Link
              to="/research"
              className="text-xs uppercase tracking-[0.15em] text-[#00D4C8] hover:text-[#33DDD3] flex items-center gap-1"
            >
              New Research <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          
          {recentTargets.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#A3B5C7] text-sm mb-4">No targets researched yet</p>
              <Link to="/research" className="btn-primary text-sm">
                Start Researching
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentTargets.map((target) => (
                <Link
                  key={target.id}
                  to={`/targets/${target.id}`}
                  className="block p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C] hover:border-[#00D4C8]/30 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[white]">{target.name}</p>
                      <p className="text-xs text-[#A3B5C7]">{target.type}</p>
                    </div>
                    {target.relevance_score && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-[#00D4C8]">{target.relevance_score}/5</span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Grant & Fund Digest Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-6 card-surface p-6"
        data-testid="grant-digest-card"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-400" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-heading text-xl text-white">Funding Intelligence</h2>
              <p className="text-xs text-[#A3B5C7]">
                {grantDigest?.total > 0
                  ? `${grantDigest.total} grants tracked, ${grantDigest.open_count || 0} open`
                  : 'No grants tracked yet'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/grants"
              className="btn-secondary text-xs flex items-center gap-1.5"
              data-testid="view-all-grants-btn"
            >
              View All Grants <ArrowRight className="w-3 h-3" />
            </Link>
            <Link
              to="/grants"
              onClick={() => setTimeout(() => document.querySelector('[data-testid="scan-grants-btn"]')?.click(), 500)}
              className="btn-primary flex items-center gap-2 text-sm"
              data-testid="grant-scan-btn"
            >
              <Radar className="w-4 h-4" strokeWidth={1.5} /> Funding Intelligence Scan
            </Link>
          </div>
        </div>

        {/* 3 Most Urgent Grants Preview */}
        {grantDigest?.urgent_grants?.length > 0 ? (
          <div className="space-y-2">
            {grantDigest.urgent_grants.map((grant, i) => {
              const isUrgent = grant.urgency === 'URGENT' || grant.urgency === 'CLOSING_SOON';
              return (
                <Link
                  key={grant.id || i}
                  to="/grants"
                  className={`block p-4 rounded-lg border transition-all hover:border-[#00D4C8]/20 ${isUrgent ? 'bg-red-500/5 border-red-500/20' : 'bg-[#0A1628] border-[#1A2F4C]'}`}
                  data-testid={`digest-grant-${i}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-white text-sm font-medium">{grant.name}</span>
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded border ${
                          grant.urgency === 'URGENT' ? 'bg-red-500/10 text-red-400 border-red-500/20'
                          : grant.urgency === 'CLOSING_SOON' ? 'bg-orange-500/10 text-orange-400 border-orange-500/20'
                          : grant.urgency === 'OPEN_ACT_SOON' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                          : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        }`}>
                          {grant.urgency?.replace('_', ' ') || 'OPEN'}
                        </span>
                      </div>
                      {grant.funder_name && <p className="text-xs text-[#A3B5C7]">{grant.funder_name}</p>}
                      {grant.mandate_summary && <p className="text-xs text-[#D4E2F0] mt-1 line-clamp-1">{grant.mandate_summary}</p>}
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      {(grant.amount_max || grant.amount_min) && (
                        <span className="text-sm text-[#00D4C8] font-medium">
                          {grant.amount_max ? `$${Number(grant.amount_max).toLocaleString()}` : `$${Number(grant.amount_min).toLocaleString()}`} {grant.amount_currency || 'USD'}
                        </span>
                      )}
                      {grant.deadline && (
                        <p className="text-xs text-[#A3B5C7] mt-1">
                          {grant.deadline}
                          {grant.days_remaining != null && ` (${grant.days_remaining}d)`}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-sm text-[#A3B5C7] mb-3">No grants discovered yet. Run your first scan to find funding opportunities.</p>
            <Link to="/grants" className="btn-secondary text-sm inline-flex items-center gap-2">
              Go to Grants <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <Link
          to="/research"
          className="card-surface p-6 hover:border-[#00D4C8]/30 transition-all group"
          data-testid="quick-action-research"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#00D4C8]/10 flex items-center justify-center">
              <Target className="w-6 h-6 text-[#00D4C8]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[white] font-medium group-hover:text-[#00D4C8] transition-colors">
                Research a Target
              </p>
              <p className="text-xs text-[#A3B5C7]">AI-powered intelligence</p>
            </div>
          </div>
        </Link>

        <Link
          to="/pipeline"
          className="card-surface p-6 hover:border-[#00D4C8]/30 transition-all group"
          data-testid="quick-action-pipeline"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[white]/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-[white]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[white] font-medium group-hover:text-[#00D4C8] transition-colors">
                View Pipeline
              </p>
              <p className="text-xs text-[#A3B5C7]">Track all opportunities</p>
            </div>
          </div>
        </Link>

        <Link
          to="/projects"
          className="card-surface p-6 hover:border-[#00D4C8]/30 transition-all group"
          data-testid="quick-action-projects"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#6BCBFF]/10 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-[#6BCBFF]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[white] font-medium group-hover:text-[#00D4C8] transition-colors">
                Projects
              </p>
              <p className="text-xs text-[#A3B5C7]">Active projects</p>
            </div>
          </div>
        </Link>

        <Link
          to="/context-import"
          className="card-surface p-6 hover:border-[#00D4C8]/30 transition-all group"
          data-testid="quick-action-import"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#00D4C8]/10 flex items-center justify-center">
              <Upload className="w-6 h-6 text-[#00D4C8]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[white] font-medium group-hover:text-[#00D4C8] transition-colors">
                Import Context
              </p>
              <p className="text-xs text-[#A3B5C7]">Extract contacts from emails &amp; notes</p>
            </div>
          </div>
        </Link>
      </motion.div>
    </div>
  );
};

export default DashboardPage;
